import { Link } from 'react-router-dom';
import type { SolCaptureSourceCard } from '../../data/sol/sol-contract';
import type { CaptureStatus } from '../../data/database.types';
import { CAPTURE_STATUS_LABEL, formatCaptureTitle } from '../captures/capture-status';
import { formatInstant } from '../../lib/timezone';
import { captureDetailPath } from './sol-capture-route';
import { projectLabel } from './sol-wording';

/**
 * One cited Capture, as a real link to the real Capture. M9 / S09-04.
 *
 * **IT NAVIGATES WITH THE CARD'S OWN PROJECT ID, NOT THE ROUTE'S**, and that is
 * the whole reason this is not two lines of inline JSX. Sol broadens: a question
 * asked inside Tampa Condo can legitimately be answered from a Capture in
 * Bayfront Tower. Building the path from the Project in the address bar would
 * send him to a Capture id that does not exist under that Project — RLS would
 * refuse it and he would see "not available" for a record he owns and Sol had
 * just quoted to him.
 *
 * **IT IS A LINK, NOT A TAPPABLE DIV** — reachable by keyboard, announced as a
 * link, and openable in a new tab, none of which a `div` with an `onClick` is.
 *
 * **NO UUID IS RENDERED AND NONE IS IN THE ACCESSIBLE NAME.** The Capture is
 * identified by its CAP number, the Project by its name or by the one safe
 * fallback. `project.name ?? project.id` is a banned pattern (`sol-wording.ts`).
 *
 * **THE CARD IS THE SERVER'S, UNALTERED.** Crewlink does not summarise it,
 * excerpt it or re-caption it — an excerpt chosen by anything other than the
 * Capture itself is another unverified claim wearing an evidence heading.
 */

function statusLabel(status: string): string {
  // The server types this loosely (`string`) because a card is generic across
  // future Sol domains. Anything outside the locked vocabulary is shown as
  // nothing rather than as a raw value.
  const known: CaptureStatus[] = ['open', 'repaired', 'closed'];
  const match = known.find((candidate) => candidate === status);
  return match ? CAPTURE_STATUS_LABEL[match] : '';
}

export interface SolSourceCardLinkProps {
  /** A CAPTURE card. A Project card is a different record and a different view. */
  readonly card: SolCaptureSourceCard;
  /**
   * The CANONICAL Crewlink timezone from the profile — never the device's, and
   * never defaulted (INV-T17). Null renders no timestamp rather than a wrong
   * one, exactly as the Captures list does.
   */
  readonly timeZone: string | null;
}

export function SolSourceCardLink({ card, timeZone }: SolSourceCardLinkProps) {
  const title = formatCaptureTitle(card.captureNumber, card.locationDisplay);
  const project = projectLabel(card.project.name);
  const status = statusLabel(card.status);
  const when = timeZone ? formatInstant(card.createdAt, timeZone) : '';

  return (
    <Link
      className="sol-source"
      to={captureDetailPath(card.project.id, card.source.id)}
      // The accessible name says what opening it does, in his vocabulary.
      aria-label={`Open source Capture ${title} in ${project}`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{title}</span>
        {status ? <span className="sol-source-status">{status}</span> : null}
      </span>
      <span className="sol-source-meta">{when ? `${project} · ${when}` : project}</span>
      {card.caption ? <span className="sol-source-caption">{card.caption}</span> : null}
      <span className="sol-source-open" aria-hidden="true">
        Open source Capture →
      </span>
    </Link>
  );
}
